"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import bitcoin from "@/public/animatedAssets/bitcoin.svg";

type State = "collapsed" | "expanded";

export const AnimatedAsset2 = ({
  className = "",
}: {
  className?: string;
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const shine = {
    collapsed: {
      backgroundPosition: ["-60% 0%", "160% 0%"],
      transition: {
        backgroundPosition: {...ease, duration: 1.2}
      }
    },
    expanded: {
      backgroundPosition: ["160% 0%", "-60% 0%"],
      transition: {
        backgroundPosition: {...ease, duration: 1.2}
      }
    },
  };

  const pillTilt = {
    collapsed: {rotate: 0, transition: ease},
    expanded: {rotate: -6, transition: ease},
  };

  const btcCenter = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: -8, transition: ease},
  };

  const btcTop = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: -16, transition: ease},
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-center justify-center px-[2rem] sm:px-[4rem]",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.p
        className="relative mb-[3rem] text-[5rem] leading-[1.1em] font-medium text-center font-funnel"
        style={{
          background: "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.4) 35%, #D9F9B9 45%, #FFFFFF 50%, #D9F9B9 55%, rgba(255,255,255,0.4) 65%, rgba(255,255,255,0.4) 100%)",
          backgroundSize: "300% 100%",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "transparent",
        }}
        variants={shine}
        animate={controls}
        initial="collapsed"
      >
        <span style={{WebkitTextFillColor: "rgba(255,255,255,0.4)", color: "rgba(255,255,255,0.4)"}}>
          New{" "}
        </span>

        <div className="relative pl-[8.5rem] inline-flex items-center bg-[#0A1A1D]">
          <motion.div
            className="left-0 bottom-0 absolute w-[7rem] h-[7rem]"
            style={{WebkitTextFillColor: "initial", color: "inherit"}}
            variants={pillTilt}
            animate={controls}
            initial="collapsed"
          >
            <div className="w-full h-full bg-[#D9F9B9]/[.25] rounded-[1.5rem] inline-flex justify-center items-center">
              <div className="w-[4rem] h-[4rem] bg-[#D9F9B9] rounded-full flex items-center justify-center">
                <Image
                  className="!w-[2rem]"
                  src={bitcoin}
                  alt="Bitcoin logo"
                />
              </div>
              <div className="top-0 right-0 -translate-y-[1rem] translate-x-[2rem] absolute w-[3.375rem] h-[3.375rem] bg-[#D9F9B9] rounded-full flex items-center justify-center text-[#222222] text-[2.25rem] leading-none">
                2
              </div>
            </div>
          </motion.div>

          <em className="font-ivy font-normal inline-flex relative leading-none">
            <motion.span
              className="absolute inset-0 text-transparent bg-clip-text bg-gradient-to-b from-[#F7FFEF] to-[#D9F9B9] text-[5rem] leading-[1.1em]"
              style={{WebkitTextFillColor: "transparent"}}
              variants={btcTop}
              animate={controls}
              initial="collapsed"
            >
              Bitcoin
            </motion.span>

            <motion.span
              className="absolute inset-0 text-transparent bg-clip-text bg-gradient-to-b from-[#F7FFEF] to-[#D9F9B9] opacity-60 text-[5rem] leading-[1.1em]"
              style={{WebkitTextFillColor: "transparent"}}
              variants={btcCenter}
              animate={controls}
              initial="collapsed"
            >
              Bitcoin
            </motion.span>

            <span
              className="text-white/40 text-[5rem] leading-[1.1em]"
              style={{WebkitTextFillColor: "rgba(255,255,255,0.4)", color: "rgba(255,255,255,0.4)"}}
            >
              Bitcoin
            </span>
          </em>
        </div>

        <span
          className="text-[5rem] leading-[1.1em]"
          style={{WebkitTextFillColor: "rgba(255,255,255,0.4)", color: "rgba(255,255,255,0.4)"}}
        >
          {" "}Analysis file available now!
        </span>
      </motion.p>
    </div>
  );
};
